import { useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  type Edge,
  type Node,
  type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Mail, MessageSquare, FileText, CreditCard, Brain, Workflow, FolderOpen, Calendar, Sparkles } from "lucide-react";
import { cls } from "../../lib/cls";
import { ExpandableGraphFrame } from "../integrations/ExpandableGraphFrame";
import type { Graph3DNode, Graph3DLink } from "../integrations/Graph3D";

/* Live picture · everyday tools → cockpit copilot → project. xyflow,
   MIT-licensed. Speaks in designer + client language only. */

type FlowNodeData = {
  label: string;
  sub?: string;
  iconKey?: "mail" | "chat" | "doc" | "pay" | "cal" | "files" | "copilot" | "project" | "memory" | "moodboard";
  tone?: "indigo" | "rose" | "amber" | "emerald" | "sky" | "violet";
};

const ICONS: Record<NonNullable<FlowNodeData["iconKey"]>, JSX.Element> = {
  mail:      <Mail           className="w-3.5 h-3.5" />,
  chat:      <MessageSquare  className="w-3.5 h-3.5" />,
  doc:       <FileText       className="w-3.5 h-3.5" />,
  pay:       <CreditCard     className="w-3.5 h-3.5" />,
  cal:       <Calendar       className="w-3.5 h-3.5" />,
  files:     <FolderOpen     className="w-3.5 h-3.5" />,
  copilot:   <Brain          className="w-3.5 h-3.5" />,
  project:   <Workflow       className="w-3.5 h-3.5" />,
  memory:    <Sparkles       className="w-3.5 h-3.5" />,
  moodboard: <Sparkles       className="w-3.5 h-3.5" />,
};
const TONES: Record<NonNullable<FlowNodeData["tone"]>, string> = {
  indigo:  "bg-indigo-50  text-indigo-700  border-indigo-300",
  rose:    "bg-rose-50    text-rose-700    border-rose-300",
  amber:   "bg-amber-50   text-amber-700   border-amber-300",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-300",
  sky:     "bg-sky-50     text-sky-700     border-sky-300",
  violet:  "bg-violet-50  text-violet-700  border-violet-300",
};

function TileNode({ data }: { data: FlowNodeData }): JSX.Element {
  return (
    <div className={cls(
      "relative px-2.5 py-2 rounded-2xl border shadow-sm min-w-[150px]",
      data.tone ? TONES[data.tone] : "bg-cream text-ink-950 border-border",
    )}>
      {/* xyflow requires explicit Handles for edges to anchor onto */}
      <Handle type="target" position={Position.Left}  className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div className="flex items-center gap-1.5">
        {data.iconKey ? ICONS[data.iconKey] : null}
        <div className="text-[12.5px] font-semibold leading-tight">{data.label}</div>
      </div>
      {data.sub ? <div className="text-[10px] mt-0.5 opacity-70">{data.sub}</div> : null}
    </div>
  );
}

const nodeTypes: NodeTypes = { tile: TileNode };

const NODES: Node<FlowNodeData>[] = [
  { id: "mail",  type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 0,   y: 0   }, data: { label: "Email",       sub: "new enquiry",       iconKey: "mail",  tone: "rose"    } },
  { id: "chat",  type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 0,   y: 80  }, data: { label: "Messaging",   sub: "client question",   iconKey: "chat",  tone: "emerald" } },
  { id: "files", type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 0,   y: 160 }, data: { label: "Files",       sub: "BOQ · floor plan",  iconKey: "files", tone: "amber"   } },
  { id: "cal",   type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 0,   y: 240 }, data: { label: "Calendar",    sub: "site visit",        iconKey: "cal",   tone: "indigo"  } },

  { id: "auto",  type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 280, y: 140 }, data: { label: "Automations", sub: "preconfigured",     iconKey: "project", tone: "sky"  } },

  { id: "agent", type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 560, y: 140 }, data: { label: "Copilot",     sub: "reads · cites · drafts", iconKey: "copilot" } },

  { id: "proj",  type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 820, y: 60  }, data: { label: "Project",     sub: "rooms · items · BOQ", iconKey: "project",   tone: "amber"  } },
  { id: "mem",   type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 820, y: 140 }, data: { label: "Memory",      sub: "decisions · approvals", iconKey: "memory",  tone: "violet" } },
  { id: "mood",  type: "tile", sourcePosition: Position.Right, targetPosition: Position.Left, position: { x: 820, y: 220 }, data: { label: "Moodboard",   sub: "style cohesion",        iconKey: "moodboard", tone: "rose" } },
];

const EDGE_DEFAULT = {
  stroke: "#94908a",
  strokeWidth: 1.6,
} as const;
const EDGE_PRIMARY = {
  stroke: "#C95C1A",
  strokeWidth: 2,
} as const;
const ARROW_DEFAULT = { type: MarkerType.ArrowClosed, color: "#94908a", width: 18, height: 18 };
const ARROW_PRIMARY = { type: MarkerType.ArrowClosed, color: "#C95C1A", width: 18, height: 18 };

const EDGES: Edge[] = [
  { id: "e1", source: "mail",  target: "auto", animated: true, type: "smoothstep", style: EDGE_DEFAULT, markerEnd: ARROW_DEFAULT },
  { id: "e2", source: "chat",  target: "auto", animated: true, type: "smoothstep", style: EDGE_DEFAULT, markerEnd: ARROW_DEFAULT },
  { id: "e3", source: "files", target: "auto", animated: true, type: "smoothstep", style: EDGE_DEFAULT, markerEnd: ARROW_DEFAULT },
  { id: "e4", source: "cal",   target: "auto", animated: true, type: "smoothstep", style: EDGE_DEFAULT, markerEnd: ARROW_DEFAULT },
  { id: "e5", source: "auto",  target: "agent", animated: true, type: "smoothstep", style: EDGE_PRIMARY, markerEnd: ARROW_PRIMARY },
  { id: "e6", source: "agent", target: "proj",  animated: true, type: "smoothstep", style: EDGE_DEFAULT, markerEnd: ARROW_DEFAULT, label: "updates rooms · items · BOQ" },
  { id: "e7", source: "agent", target: "mem",   animated: true, type: "smoothstep", style: EDGE_DEFAULT, markerEnd: ARROW_DEFAULT, label: "writes decisions" },
  { id: "e8", source: "agent", target: "mood",  animated: true, type: "smoothstep", style: EDGE_DEFAULT, markerEnd: ARROW_DEFAULT, label: "tags style" },
];

export function IntegrationFlowGraph(): JSX.Element {
  const [nodes] = useState<Node<FlowNodeData>[]>(NODES);
  const [edges] = useState<Edge[]>(EDGES);
  const inbound = useMemo(() => EDGES.filter((e) => e.target === "auto").length, []);

  return (
    <div className="rounded-2xl border border-border/70 bg-gradient-to-br from-cream via-cream to-cream-dark/30 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)] overflow-hidden">
      <div className="px-3 pt-3 pb-2 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Workflow className="w-3 h-3" /> The picture · everyday tools → your copilot → the project
          </div>
          <div className="text-[12.5px] text-ink-700 mt-0.5">
            Every email, message, file and payment flows into one place and comes back as a grounded next step.
          </div>
        </div>
        <span className="pill bg-cream-dark border border-border text-ink-700 text-[10.5px] tabular">
          {inbound} apps wired in
        </span>
      </div>
      <ExpandableGraphFrame
        title="Integration topology"
        subtitle={`${inbound} inbound app connectors · the everyday-tools → copilot → project picture`}
        graph3d={{
          nodes: NODES.map<Graph3DNode>((n) => ({ id: n.id, label: (n.data as { label: string }).label, kind: (n.data as { tone?: string }).tone ?? "thing" })),
          links: EDGES.map<Graph3DLink>((e) => ({
            source: String(e.source),
            target: String(e.target),
            ...(typeof e.label === "string" ? { label: e.label } : {}),
            emphasis: "primary" as const,
          })),
        }}
      >
        <div className="border-t border-border/50" style={{ height: 360, background: "radial-gradient(circle at 30% 30%, rgba(201,92,26,0.04), transparent 70%)" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            proOptions={{ hideAttribution: true }}
            panOnScroll
          >
            <Background gap={20} size={1} color="#ece7dc" />
            <Controls showInteractive={false} className="!shadow-sm" />
          </ReactFlow>
        </div>
      </ExpandableGraphFrame>
    </div>
  );
}
